import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, TouchableWithoutFeedback } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { Colors, Shadows, Spacing, Radius, FontSize, FontWeight } from '../theme/tokens';
import { Icon } from '../atoms/Icon';

export interface ConfirmDeleteModalProps {
  visible: boolean;
  itemTitle?: string;
  onConfirm: (deleteData: boolean) => void;
  onClose: () => void;
}

export const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  visible,
  itemTitle = 'this transfer',
  onConfirm,
  onClose,
}) => {
  const { colors } = useTheme();
  const [deleteData, setDeleteData] = useState(false);

  const handleConfirm = () => {
    onConfirm(deleteData);
    onClose();
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.overlay}>
          <TouchableWithoutFeedback>
            <View style={[styles.modal, { backgroundColor: colors.surface, borderColor: colors.border }]}>
              {/* Title & Body */}
              <Text style={[styles.title, { color: colors.text }]}>Remove Transfer?</Text>
              <Text style={[styles.body, { color: colors.subtext }]}>
                Are you sure you want to remove <Text style={{ fontWeight: FontWeight.bold, color: colors.text }}>{itemTitle}</Text>? This will stop active peer-to-peer sharing for this item.
              </Text>

              {/* Toggle option: Delete data from disk */}
              <TouchableOpacity
                style={[styles.checkboxRow, { backgroundColor: colors.pill }]}
                onPress={() => setDeleteData(!deleteData)}
                activeOpacity={0.8}
              >
                <View style={[styles.checkbox, { borderColor: colors.border, backgroundColor: deleteData ? colors.brand : 'transparent' }]}>
                  {deleteData && <Icon name="check" size={12} color={colors.brandText} />}
                </View>
                <Text style={[styles.checkboxLabel, { color: colors.text }]}>Also delete downloaded files from disk</Text>
              </TouchableOpacity>

              {/* Actions */}
              <View style={styles.actionsRow}>
                <TouchableOpacity
                  style={[styles.btn, styles.cancelBtn, { borderColor: colors.border, backgroundColor: colors.surface }]}
                  onPress={onClose}
                  activeOpacity={0.8}
                >
                  <Text style={[styles.btnText, { color: colors.text }]}>Cancel</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.btn, styles.deleteBtn, { backgroundColor: colors.brand }]}
                  onPress={handleConfirm}
                  activeOpacity={0.85}
                >
                  <Icon name="trash" size={16} color={colors.brandText} />
                  <Text style={[styles.btnText, { color: colors.brandText }]}>Remove</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent:  'center',
    alignItems:      'center',
    zIndex:          1000,
  },
  modal: {
    width:        '90%',
    maxWidth:     380,
    borderRadius: Radius.xl,
    borderWidth:  1,
    padding:      Spacing.lg,
    ...Shadows.card,
  },
  header: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   Spacing.md,
  },
  iconBadge: {
    width:          40,
    height:         40,
    borderRadius:   Radius.full,
    justifyContent: 'center',
    alignItems:     'center',
  },
  closeBtn: {
    padding: Spacing.xs,
  },
  title: {
    fontSize:     FontSize.lg,
    fontWeight:   FontWeight.bold,
    marginBottom: Spacing.xs,
  },
  body: {
    fontSize:     FontSize.sm,
    lineHeight:   20,
    marginBottom: Spacing.md,
  },
  checkboxRow: {
    flexDirection:  'row',
    alignItems:     'center',
    padding:        Spacing.md,
    borderRadius:   Radius.md,
    marginBottom:   Spacing.lg,
  },
  checkbox: {
    width:          18,
    height:         18,
    borderRadius:   Radius.xs,
    borderWidth:    1.5,
    justifyContent: 'center',
    alignItems:     'center',
    marginRight:    Spacing.sm,
  },
  checkboxLabel: {
    fontSize:   FontSize.xs,
    fontWeight: FontWeight.medium,
    flex:       1,
  },
  actionsRow: {
    flexDirection: 'row',
    gap:           Spacing.sm,
  } as any,
  btn: {
    flex:              1,
    height:            44,
    borderRadius:      Radius.md,
    flexDirection:     'row',
    alignItems:        'center',
    justifyContent:    'center',
    paddingHorizontal: Spacing.md,
    gap:               Spacing.xs,
  } as any,
  cancelBtn: {
    borderWidth: 1,
  },
  deleteBtn: {},
  btnText: {
    fontSize:   FontSize.sm,
    fontWeight: FontWeight.bold,
  },
});
